import React, { useState, useEffect } from "react";
import './style.css';
import { useSelector } from "react-redux";
import { ROLES } from "../../constants/textconstants";

interface PrepopulatedData {
  assignee: string;
  from: number;
  to: number;
  certificatePrefix?: string;
}

interface User {
  user_id: string; // Changed from userId to user_id to match actual data
  username: string;
  territories?: { code: string }[];
  units?: { code: string }[];
  squads?: { code: string }[];
}

interface AssignCertificateModalProps {
  onClose: () => void;
  onSubmit: (formData: any) => void;
  users: User[];

  prepopulatedData?: PrepopulatedData | null;
}

const AssignCertificateModal: React.FC<AssignCertificateModalProps> = ({
  onClose,
  onSubmit,
  users,
  prepopulatedData
}) => {
  const isEditMode = !!prepopulatedData;

  const getInitialAssigneeValue = (data: any, allUsers: User[], role: string) => {
    if (!data || !data.assignee) return '';
    // prepopulatedData.assignee is now expected to be the full string "user_id-code"
    return data.assignee; 
  };



  const userData = useSelector((state: any) => state.formConfig.user);


  const initialAssignee = getInitialAssigneeValue(prepopulatedData, users, userData.role); 
  const [assignee, setAssignee] = useState(initialAssignee)
  const [from, setFrom] = useState(String(prepopulatedData?.from || ''));
  const [to, setTo] = useState(String(prepopulatedData?.to || ''));
  const [certificatePrefix, setCertificatePrefix] = useState(prepopulatedData?.certificatePrefix || '');
  const [prefixError, setPrefixError] = useState('');
  const [fromError, setFromError] = useState('');
  const [toError, setToError] = useState('');
  useEffect(() => {
    const newInitialAssignee = getInitialAssigneeValue(prepopulatedData, users, userData.role); 
    setAssignee(newInitialAssignee);
    setFrom(String(prepopulatedData?.from || ''));
    setTo(String(prepopulatedData?.to || ''));
    setCertificatePrefix(prepopulatedData?.certificatePrefix || '');
    setPrefixError('');
  }, [prepopulatedData, users, userData.role]);

  const prefixRegex = /^[a-zA-Z0-9/\-()]+$/;

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFrom(value);

    if (!isEditMode) {
      setTo('');
      setToError('');
    }

    if (value && parseInt(value) > 0) {
      setFromError('');
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTo(value);

    if (from && parseInt(value) <= parseInt(from)) {
      setToError('To must be higher than From.');
    } else {
      setToError('');
    }
  };



  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only letters, digits, slash, dash, parentheses
    const sanitized = value.replace(/[^a-zA-Z0-9/\-()]/g, '');
    setCertificatePrefix(sanitized);
    if (sanitized && !prefixRegex.test(sanitized)) {
      setPrefixError('Only letters, numbers, /, -, ( and ) are allowed');
    } else {
      setPrefixError('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (from === '' || to === '' || assignee === '') {
      setFromError(from === '' ? 'From is required.' : '');
      setToError(to === '' ? 'To is required.' : '');
      return;
    }

    if (certificatePrefix && !prefixRegex.test(certificatePrefix)) {
      setPrefixError('Only letters, numbers, /, -, ( and ) are allowed');
      return;
    }

    if (toError || fromError || prefixError) return;

    const formData = {
      assignee,
      from: parseInt(from),
      to: parseInt(to),
      certificatePrefix: certificatePrefix || '',
      mode: isEditMode ? 'EDIT' : 'ASSIGN'
    };

    onSubmit(formData);
    onClose();
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-headers" style={{position:'relative'}}>
          <span className="modal-heading">{isEditMode ? 'Edit Assignment' : 'Assign Booklet'}</span>
          <button className="modal-close-button" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="modallabel">Assign To <mark style={{ color: 'red', background: 'transparent' }}>*</mark></label>
            {userData.role === ROLES.APPROVER ? ( // Changed userData.jobTitle to userData.role
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} required>
                <option value="">Select an Issuer</option>
                {[...users].sort((a: any, b: any) => a.username.localeCompare(b.username)).map((user: any) => (
                  <option key={user.user_id} value={`${user.user_id}`}>{user.username}</option>
                ))}
              </select>
            ) : (
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} required>
                <option value="">Select an Approver</option>
                {[...users].sort((a: any, b: any) => a.username.localeCompare(b.username)).map((user: any) => (
                  <option key={user.user_id} value={`${user.user_id}`}>{user.username}</option>
                ))}
              </select>
            )}

          </div>

          
          <div className="form-group">
            <label className="modallabel">Certificate Prefix</label>
            <input
              type="text"
              placeholder="e.g. MH/2026"
              value={certificatePrefix}
              onChange={handlePrefixChange}
              maxLength={50}
            />
            {prefixError && <p className="error-message">{prefixError}</p>}
            {certificatePrefix && !prefixError && from && (
              <p style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                Preview: <strong>{certificatePrefix} {from}</strong>
                {to && <> to <strong>{certificatePrefix} {to}</strong></>}
              </p>
            )}
          </div>
          

          <div className="form-group">
            <label className="modallabel">Certificate no. Range  <mark style={{ color: 'red', background: 'transparent' }}>*</mark></label>
            <div className="range-inputs">
              <input
                type="number"
                placeholder="From"
                value={from}
                onChange={handleFromChange}
                required
              />
              <span>-</span>
              <input
                type="number"
                placeholder="To"
                value={to}
                onChange={handleToChange}
                disabled={!from}
                required
              />
            </div>
            {toError && <p className="error-message">{toError}</p>}
          </div>

          <div className="form-actions">
            <button type="submit" className="assign-button-modal">
              {isEditMode ? 'Save Changes' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignCertificateModal;